import { exportEwaybillRecord, getSharedEwaybillShippingAddressDetails } from "@/services/api/details";
import { Card, Form, message, Modal, Table } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import SearchBar, { FormValues } from "./SearchBar";
import useColumns from "./useColumns";
import { usePagination, useRequest } from "ahooks";
import UserType from "@/enum/UserType";
import { useParams } from "react-router-dom";
import { getConsumerInfos } from "@/services/api/sharedEwaybillShippingAddress";

interface SharedEwaybillProviderDetailsProps {}

const SharedEwaybillProviderDetails: React.FC<SharedEwaybillProviderDetailsProps> = function (props) {
  const [formValues, setFormValues] = useState<FormValues>({});
  const [form] = Form.useForm<FormValues>();
  const params = useParams();

  useEffect(() => {
    // /shareEwaybillToOthers/details/:ewaybillAccountId/:consumerId/:logisticsCompany/:sharedShippingAddressesId
    const ewaybillAccountId = Number(params["ewaybillAccountId"]);
    const logisticsCompany = params["logisticsCompany"];

    form.setFieldsValue({
      ewaybillAccountId: !Number.isNaN(ewaybillAccountId) ? ewaybillAccountId : undefined,
      logisticsCompany: logisticsCompany !== "all" ? logisticsCompany : undefined,
    });
  }, []);

  const { data: consumerInfos } = useRequest(getConsumerInfos);

  const consumerInfoMapping = useMemo(() => {
    const map = new Map<number, number>();

    consumerInfos?.forEach(({ id, companyId }) => {
      map.set(id, companyId);
    });

    return map;
  }, [consumerInfos]);

  useEffect(() => {
    if (consumerInfos) {
      const consumerCompanyId = Number(params["consumerCompanyId"]);
      const sharedShippingAddressesId = Number(params["sharedShippingAddressesId"]);

      if (!Number.isNaN(sharedShippingAddressesId)) {
        const consumerId = consumerInfos.find(({ id }) => id === sharedShippingAddressesId)?.id;

        form.setFieldsValue({
          consumerId: consumerId ? consumerId : null,
        });
      } else if (!Number.isNaN(consumerCompanyId)) {
        const consumerId = consumerInfos.find(({ companyId }) => companyId === consumerCompanyId)?.id;

        form.setFieldsValue({
          consumerId: consumerId ? consumerId : null,
        });
      }
    }
  }, [consumerInfos]);

  const { data, loading, pagination, run } = usePagination(
    async ({ current, pageSize }) => {
      const { rangeDate, consumerId, ...otherValues } = form.getFieldsValue();

      const startTime = rangeDate?.[0]?.format("YYYY-MM-DD HH:mm:ss");
      const endTime = rangeDate?.[1]?.format("YYYY-MM-DD HH:mm:ss");

      let consumerCompanyId = params["consumerCompanyId"] !== "all" ? Number(params["consumerCompanyId"]) : null;

      if (consumerId !== undefined && consumerId !== null) {
        consumerCompanyId = consumerInfoMapping.get(consumerId);
      }

      const results = await getSharedEwaybillShippingAddressDetails({
        ...otherValues,
        consumerCompanyId,
        startTime,
        endTime,
        pageNo: current,
        pageSize,
        userType: UserType.PROVIDER,
      });

      return {
        list: results.content,
        total: results.totalElements,
      };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [formValues],
    }
  );

  const columns = useColumns(pagination?.current, pagination?.pageSize);

  return (
    <Card>
      <SearchBar
        consumerInfos={consumerInfos}
        form={form}
        onReset={() => {
          form.resetFields();

          setFormValues({});
        }}
        onSearch={() => {
          const values = form.getFieldsValue();

          setFormValues(values);
        }}
        onExport={() => {
          Modal.confirm({
            title: "您确认导出该时间段的打印明细吗",
            onOk: async () => {
              const { rangeDate, consumerId, ...otherValues } = form.getFieldsValue();

              const startTime = rangeDate?.[0]?.format("YYYY-MM-DD HH:mm:ss");
              const endTime = rangeDate?.[1]?.format("YYYY-MM-DD HH:mm:ss");

              let consumerCompanyId;

              if (consumerId !== undefined && consumerId !== null) {
                consumerCompanyId = consumerInfoMapping.get(consumerId);
              }

              await exportEwaybillRecord({
                ...otherValues,
                consumerCompanyId,
                startTime,
                endTime,
                userType: UserType.PROVIDER,
              });

              message.success("导出任务已创建，稍后请前往导出记录查看并下载");
            },
          });
        }}
      />

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={data?.list}
        pagination={{
          ...pagination,
          showQuickJumper: true,
          showTotal: (total) => `总共 ${total} 个项目`,
          showSizeChanger: true,
          pageSizeOptions: ["10", "50", "100", "200"],
        }}
      />
    </Card>
  );
};

export default React.memo(SharedEwaybillProviderDetails);
